import type { FC } from 'react';

import { useCms, useContent, useSetting } from '@eneco/cms';
import RichTextComponent from '@eneco/components/RichText/RichText';
import { Checklist as ChecklistComponent } from '@eneco/components/ui/Checklist/Checklist';
import { Text } from '@eneco/sparky';

import type { ChecklistContent } from './checklist-content.types';

/**
 * `Checklist` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy component (`libs/sitecore/components/content/src/Checklist/Checklist.tsx`): a list
 * of rich-text items, each with a checkmark — a filled one with extra spacing when `isFilledCheckMark`
 * is set, an outline one otherwise.
 *
 * Sitecore sends each item as an HTML string; Sanity as a `ChecklistItem` whose `text` the adapter has
 * already turned into HTML.
 */
const Checklist: FC = () => {
  const { adapter } = useCms();
  const content = useContent<ChecklistContent>();
  // Sitecore's Rendering Parameters Template names this `isFilledCheckmark` (lowercase `m`); the Sanity
  // schema capitalises it (`isFilledCheckMark`). `useSetting` does an exact-string lookup with no
  // case-folding (confirmed), and the Sitecore lane's own boolean-ish param serializes as `'1'`.
  const isFilledCheckMarkSanity = useSetting('isFilledCheckMark');
  const isFilledCheckMarkSitecore = useSetting('isFilledCheckmark');
  const isFilledCheckMark = isFilledCheckMarkSanity ?? isFilledCheckMarkSitecore;
  const isFilledCheckmark = isFilledCheckMark === 'true' || isFilledCheckMark === '1';

  const gap = isFilledCheckmark ? '5' : '1';
  const gapItem = isFilledCheckmark ? '3' : '1';
  const icon = isFilledCheckmark ? 'checkmark-filled' : undefined;
  const fontWeight = isFilledCheckmark ? 'bold' : undefined;

  const items = content.items ?? [];

  return (
    <ChecklistComponent gap={gap}>
      {items.map((item, index) => {
        const html = adapter.toRichText(typeof item === 'string' ? item : item?.text);
        const key = (typeof item === 'string' ? item : item?._key) || String(index);
        return (
          <ChecklistComponent.Item key={key} icon={icon} gap={gapItem}>
            <Text size={{ initial: 'BodyM', md: 'BodyL' }} weight={fontWeight}>
              {html ? <RichTextComponent html={html} /> : null}
            </Text>
          </ChecklistComponent.Item>
        );
      })}
    </ChecklistComponent>
  );
};

export default Checklist;
